import { useState } from "react";
import { Button, Card, InputField, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { ConfirmDialog } from "../ConfirmDialog";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import type { OrganizationSecret } from "../../redux/reducers/organizationSecretsSlice";

type OrganizationSecretsCardProps = {
  secrets: OrganizationSecret[];
  onAdd: (name: string, value: string) => void;
  onUpdate: (id: string, data: { name?: string; value?: string }) => void;
  onDelete: (id: string) => Promise<void>;
};

export const OrganizationSecretsCard = ({ secrets, onAdd, onUpdate, onDelete }: OrganizationSecretsCardProps) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editValue, setEditValue] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleAdd = () => {
    if (newName.trim() && newValue.trim()) {
      onAdd(newName.trim(), newValue.trim());
      setNewName("");
      setNewValue("");
      setShowAddForm(false);
    }
  };

  const handleUpdate = (id: string) => {
    const updates: { name?: string; value?: string } = {};
    if (editName.trim()) updates.name = editName.trim();
    if (editValue.trim()) updates.value = editValue.trim();
    if (Object.keys(updates).length > 0) {
      onUpdate(id, updates);
    }
    setEditingId(null);
    setEditName("");
    setEditValue("");
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await onDelete(deleteId);
      setDeleteId(null);
    }
  };

  return (
    <OrganizationSettingsTab
      title="Secrets"
      count={secrets.length}
    >
      <View className="flex flex-col gap-3">
        <View className="flex flex-row items-center justify-between">
          <View className="flex flex-col gap-1">
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              Organization-managed secrets. These can be referenced by evaluators to use your own provider API keys.
            </Text>
          </View>
        </View>

        {secrets.map((secret) => (
          <Card
            key={secret.id}
            className="p-4"
          >
            {editingId === secret.id ? (
              <View className="flex flex-col gap-3">
                <InputField
                  label="Name"
                  value={editName}
                  onChange={setEditName}
                  placeholder="Key name"
                />
                <InputField
                  label="New Value (leave blank to keep current)"
                  value={editValue}
                  onChange={setEditValue}
                  placeholder="Enter new API key value"
                  type="password"
                />
                <View className="flex flex-row gap-2 justify-end">
                  <Button
                    variant="neutral"
                    size="auto"
                    onClick={() => {
                      setEditingId(null);
                      setEditName("");
                      setEditValue("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="success"
                    size="auto"
                    onClick={() => handleUpdate(secret.id)}
                  >
                    <Icon
                      name="check"
                      size={14}
                    />
                    Save
                  </Button>
                </View>
              </View>
            ) : (
              <View className="flex flex-row items-center justify-between">
                <View className="flex flex-row items-center gap-3">
                  <Icon
                    name="key"
                    size={16}
                    className="text-gray-400"
                  />
                  <View className="flex flex-col">
                    <Text className="text-sm font-medium text-gray-900 dark:text-white">{secret.name}</Text>
                    <Text className="text-xs text-gray-400 dark:text-gray-500">••••••••</Text>
                  </View>
                </View>
                <View className="flex flex-row items-center gap-2">
                  <Button
                    variant="neutral"
                    size="auto"
                    onClick={() => {
                      setEditingId(secret.id);
                      setEditName(secret.name);
                      setEditValue("");
                    }}
                  >
                    <Icon
                      name="edit"
                      size={14}
                    />
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="auto"
                    onClick={() => setDeleteId(secret.id)}
                  >
                    <Icon
                      name="trash"
                      size={14}
                    />
                  </Button>
                </View>
              </View>
            )}
          </Card>
        ))}

        {showAddForm ? (
          <Card className="p-4 border-dashed">
            <View className="flex flex-col gap-3">
              <InputField
                label="Name"
                value={newName}
                onChange={setNewName}
                placeholder="e.g. openai-production"
              />
              <InputField
                label="API Key Value"
                value={newValue}
                onChange={setNewValue}
                placeholder="Enter API key"
                type="password"
              />
              <View className="flex flex-row gap-2 justify-end">
                <Button
                  variant="neutral"
                  size="auto"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewName("");
                    setNewValue("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  size="auto"
                  onClick={handleAdd}
                  disabled={!newName.trim() || !newValue.trim()}
                >
                  <Icon
                    name="add"
                    size={14}
                  />
                  Add Key
                </Button>
              </View>
            </View>
          </Card>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex flex-row items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-lg px-3 py-2.5 group border border-dashed border-gray-300 dark:border-gray-600"
          >
            <View className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={13}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              Add secret
            </Text>
          </button>
        )}
      </View>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Secret"
        message="Are you sure you want to delete this secret? Evaluators referencing this secret will fail at runtime."
      />
    </OrganizationSettingsTab>
  );
};
